import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  aiQuickActions,
  aiInitialHistory,
  aiTypeLabel,
  aiTypeIcon,
  aiTypeColor,
  getAIResponse,
  type AIFunctionType,
  type AIHistoryEntry,
} from '@/mocks/superAdminAIAssistant';

interface ChatMessage {
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
  type?: AIFunctionType;
  editing: boolean;
  editedContent: string;
}

const categoryLabels: Record<string, string> = {
  Tickets: 'Tickets',
  Radios: 'Radios',
  Marketing: 'Marketing',
  Contenido: 'Contenido',
  Legal: 'Legal',
};

const groupedActions = aiQuickActions.reduce<Record<string, typeof aiQuickActions>>((acc, action) => {
  if (!acc[action.category]) acc[action.category] = [];
  acc[action.category].push(action);
  return acc;
}, {});

export default function SuperAdminAIAssistant() {
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [history, setHistory] = useState<AIHistoryEntry[]>(aiInitialHistory);
  const [queryInput, setQueryInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Tickets');
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const detectType = (q: string): AIFunctionType => {
    const lower = q.toLowerCase();
    if (lower.includes('resumir') || lower.includes('tickets abiertos') || (lower.includes('ticket') && !lower.includes('suger') && !lower.includes('respuesta'))) return 'ticket_summary';
    if (lower.includes('sugerir') || lower.includes('sugerencia') || lower.includes('respuesta')) return 'response_suggestion';
    if (lower.includes('riesgo') || lower.includes('abandono') || lower.includes('inactividad')) return 'risk_detection';
    if (lower.includes('campaña') || lower.includes('marketing') || lower.includes('lanzar')) return 'campaign_suggestion';
    if (lower.includes('analizar') || lower.includes('actividad') || lower.includes('métrica')) return 'activity_analysis';
    if (lower.includes('stream') || lower.includes('caído') || lower.includes('intermitente')) return 'stream_review';
    if (lower.includes('masivo') || lower.includes('broadcast') || lower.includes('comunicado')) return 'mass_message';
    if (lower.includes('email') || lower.includes('correo') || lower.includes('precios')) return 'professional_email';
    if (lower.includes('cláusula') || lower.includes('legal') || lower.includes('términos') || lower.includes('datos')) return 'legal_clause';
    if (lower.includes('contrato') || lower.includes('revisar contrato')) return 'contract_review';
    return 'activity_analysis';
  };

  const processQuery = (q: string, fnType?: AIFunctionType) => {
    if (!q.trim() || isThinking) return;
    setIsThinking(true);

    const userMsg: ChatMessage = {
      role: 'user',
      content: q,
      timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      editing: false,
      editedContent: '',
    };
    setChatHistory((prev) => [...prev, userMsg]);
    setQueryInput('');

    const type = fnType || detectType(q);

    setTimeout(() => {
      const response = getAIResponse(type, q);
      const aiMsg: ChatMessage = {
        role: 'ai',
        content: response,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        type,
        editing: false,
        editedContent: '',
      };

      setChatHistory((prev) => [...prev, aiMsg]);

      const historyEntry: AIHistoryEntry = {
        id: `h${Date.now()}`,
        type,
        title: aiTypeLabel[type],
        query: q,
        result: response,
        timestamp: new Date().toLocaleString('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }),
      };
      setHistory((prev) => [historyEntry, ...prev]);
      setIsThinking(false);
      scrollToBottom();
    }, 1500 + Math.random() * 1200);
  };

  const handleQuickAction = (prompt: string, type: AIFunctionType) => {
    processQuery(prompt, type);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processQuery(queryInput);
  };

  const copyResult = async (content: string, msg: ChatMessage) => {
    const textToCopy = msg.editing && msg.editedContent ? msg.editedContent : content;
    try {
      await navigator.clipboard.writeText(textToCopy);
      Swal.fire({
        icon: 'success',
        title: 'Copiado',
        text: 'El resultado se copió al portapapeles.',
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar al portapapeles.',
        confirmButtonColor: '#1a1a1a',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
      });
    }
  };

  const toggleEdit = (idx: number) => {
    setChatHistory((prev) =>
      prev.map((msg, i) => {
        if (i !== idx) return msg;
        if (msg.editing) {
          return { ...msg, editing: false, editedContent: '' };
        }
        return { ...msg, editing: true, editedContent: msg.content };
      }),
    );
  };

  const saveEdit = (idx: number) => {
    setChatHistory((prev) =>
      prev.map((msg, i) => {
        if (i !== idx || !msg.editing) return msg;
        return { ...msg, content: msg.editedContent, editing: false, editedContent: '' };
      }),
    );
    Swal.fire({
      icon: 'success',
      title: 'Guardado',
      text: 'El resultado fue actualizado.',
      timer: 1500,
      showConfirmButton: false,
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
    });
  };

  const cancelEdit = (idx: number) => {
    setChatHistory((prev) =>
      prev.map((msg, i) => {
        if (i !== idx) return msg;
        return { ...msg, editing: false, editedContent: '' };
      }),
    );
  };

  const openCampaignModal = (content: string, msg: ChatMessage) => {
    const textToUse = msg.editing && msg.editedContent ? msg.editedContent : content;
    const preview = textToUse.length > 200 ? textToUse.substring(0, 200) + '...' : textToUse;
    Swal.fire({
      title: '¿Usar en campaña?',
      html: `<p class="text-xs text-left text-gray-500 mb-2">Este contenido se usará como base para una nueva campaña en Marketing.</p><div class="bg-gray-50 rounded-xl p-3 text-left text-[10px] text-gray-600 max-h-32 overflow-y-auto whitespace-pre-line">${preview}</div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ir a Campañas',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#9ca3af',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/sa/marketing');
      }
    });
  };

  const openTicketModal = (content: string, msg: ChatMessage) => {
    const textToUse = msg.editing && msg.editedContent ? msg.editedContent : content;
    const preview = textToUse.length > 200 ? textToUse.substring(0, 200) + '...' : textToUse;
    Swal.fire({
      title: '¿Usar en ticket?',
      html: `<p class="text-xs text-left text-gray-500 mb-2">Este contenido se usará como respuesta en un ticket de soporte.</p><div class="bg-gray-50 rounded-xl p-3 text-left text-[10px] text-gray-600 max-h-32 overflow-y-auto whitespace-pre-line">${preview}</div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ir a Soporte',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#9ca3af',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/sa/support');
      }
    });
  };

  const loadFromHistory = (entry: AIHistoryEntry) => {
    setSelectedHistoryId(entry.id);
    setChatHistory([
      {
        role: 'user',
        content: entry.query,
        timestamp: entry.timestamp,
        editing: false,
        editedContent: '',
      },
      {
        role: 'ai',
        content: entry.result,
        timestamp: entry.timestamp,
        type: entry.type,
        editing: false,
        editedContent: '',
      },
    ]);
    scrollToBottom();
  };

  const deleteHistoryItem = (id: string) => {
    Swal.fire({
      title: '¿Eliminar del historial?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#9ca3af',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    }).then((result) => {
      if (result.isConfirmed) {
        setHistory((prev) => prev.filter((h) => h.id !== id));
        if (selectedHistoryId === id) setSelectedHistoryId(null);
        Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          timer: 1200,
          showConfirmButton: false,
          customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
        });
      }
    });
  };

  const clearChat = () => {
    if (chatHistory.length === 0) return;
    Swal.fire({
      title: '¿Limpiar chat?',
      text: 'Se borrará la conversación actual. El historial se mantiene.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Limpiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6b7280',
      cancelButtonColor: '#9ca3af',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    }).then((result) => {
      if (result.isConfirmed) {
        setChatHistory([]);
        setSelectedHistoryId(null);
      }
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const getDisplayContent = (msg: ChatMessage): string => {
    if (msg.editing && msg.editedContent) return msg.editedContent;
    return msg.content;
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-4 max-w-[1600px] mx-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">AI Assistant</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">AI Assistant</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Asistente IA interno · {history.length} consultas en historial
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                <i className="ri-information-line text-[9px]"></i>Modo simulación
              </span>
            </div>

            {/* Quick Actions by Category */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2 px-1">Acciones rápidas</p>
              <div className="space-y-2">
                {Object.entries(groupedActions).map(([category, actions]) => (
                  <div key={category}>
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                      className="flex items-center gap-1.5 text-[10px] font-semibold text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer mb-1 px-1"
                    >
                      <i className={`${expandedCategory === category ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-xs`}></i>
                      {categoryLabels[category] || category}
                      <span className="text-foreground-300 font-normal">({actions.length})</span>
                    </button>
                    {expandedCategory === category && (
                      <div className="flex flex-wrap gap-1.5 pl-1">
                        {actions.map((action) => (
                          <button
                            key={action.id}
                            onClick={() => handleQuickAction(action.prompt, action.type)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-[10px] font-medium hover:bg-background-200 hover:border-background-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className={`${action.icon} text-xs`}></i>
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Main two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Chat Panel */}
              <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl flex flex-col h-[550px] lg:h-[620px]">
                <div className="px-4 py-3 border-b border-background-200 shrink-0 flex items-center justify-between">
                  <div>
                    <p className="text-foreground-800 text-xs font-semibold">Chat con AI</p>
                    <p className="text-foreground-400 text-[10px]">Consultá lo que necesites sobre la plataforma</p>
                  </div>
                  {chatHistory.length > 0 && (
                    <button
                      onClick={clearChat}
                      className="text-foreground-300 hover:text-foreground-500 transition-colors cursor-pointer"
                      title="Limpiar chat"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  {chatHistory.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                        <i className="ri-robot-3-line text-foreground-300 text-2xl"></i>
                      </div>
                      <p className="text-foreground-600 text-xs font-semibold">AI Assistant listo</p>
                      <p className="text-foreground-400 text-[10px] mt-1 max-w-xs mx-auto">
                        Elegí una acción rápida o escribí tu consulta. Puedo resumir tickets, detectar riesgos, generar campañas, revisar streams, redactar emails, y más.
                      </p>
                    </div>
                  )}

                  {chatHistory.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] ${msg.role === 'user' ? '' : 'w-full'}`}>
                        {msg.role === 'user' ? (
                          <div className="px-3.5 py-2.5 rounded-2xl bg-foreground-950 text-background-50 rounded-br-md">
                            <p className="text-[10px] font-medium">Tú</p>
                            <p className="text-[10px] leading-relaxed mt-0.5">{msg.content}</p>
                            <p className="text-[8px] mt-1 text-background-50/60">{msg.timestamp}</p>
                          </div>
                        ) : (
                          <div className="bg-background-100 border border-background-200 rounded-2xl rounded-bl-md overflow-hidden">
                            <div className="px-3.5 py-2.5 border-b border-background-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-lg bg-foreground-950 flex items-center justify-center">
                                  <i className="ri-robot-3-line text-background-50 text-[9px]"></i>
                                </div>
                                <span className="text-[10px] font-semibold text-foreground-800">AI Assistant</span>
                                {msg.type && (
                                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium ${aiTypeColor[msg.type]}`}>
                                    <i className={`${aiTypeIcon[msg.type]} text-[8px] mr-0.5`}></i>
                                    {aiTypeLabel[msg.type]}
                                  </span>
                                )}
                              </div>
                              <span className="text-[8px] text-foreground-400">{msg.timestamp}</span>
                            </div>

                            {msg.editing ? (
                              <div className="p-3.5 space-y-2">
                                <textarea
                                  value={msg.editedContent}
                                  onChange={(e) => {
                                    setChatHistory((prev) =>
                                      prev.map((m, i) => (i === idx ? { ...m, editedContent: e.target.value } : m)),
                                    );
                                  }}
                                  className="w-full min-h-[120px] px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-[10px] text-foreground-800 leading-relaxed resize-y outline-none focus:border-foreground-300 transition-colors"
                                />
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => saveEdit(idx)}
                                    className="px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-check-line text-xs mr-1"></i>Guardar
                                  </button>
                                  <button
                                    onClick={() => cancelEdit(idx)}
                                    className="px-3 py-1.5 rounded-lg bg-background-200 text-foreground-600 text-[10px] font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="p-3.5">
                                <p className="text-[10px] leading-relaxed text-foreground-800 whitespace-pre-line">{msg.content}</p>
                              </div>
                            )}

                            <div className="px-3.5 py-2 border-t border-background-200 flex items-center gap-1 flex-wrap">
                              <button
                                onClick={() => toggleEdit(idx)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-medium transition-colors cursor-pointer whitespace-nowrap ${msg.editing ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-500 hover:bg-background-300'}`}
                              >
                                <i className={`${msg.editing ? 'ri-edit-line' : 'ri-edit-line'} text-[9px]`}></i>
                                {msg.editing ? 'Editando' : 'Editar'}
                              </button>
                              <button
                                onClick={() => copyResult(msg.content, msg)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-200 text-foreground-500 text-[9px] font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-file-copy-line text-[9px]"></i>
                                Copiar
                              </button>
                              <button
                                onClick={() => openCampaignModal(msg.content, msg)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[9px] font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-megaphone-line text-[9px]"></i>
                                Usar en campaña
                              </button>
                              <button
                                onClick={() => openTicketModal(msg.content, msg)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-50 text-sky-600 text-[9px] font-medium hover:bg-sky-100 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-customer-service-2-line text-[9px]"></i>
                                Usar en ticket
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {isThinking && (
                    <div className="flex justify-start">
                      <div className="bg-background-100 border border-background-200 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-5 h-5 rounded-lg bg-foreground-950 flex items-center justify-center">
                            <i className="ri-robot-3-line text-background-50 text-[9px]"></i>
                          </div>
                          <span className="text-[10px] font-semibold text-foreground-500">AI Assistant</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          <span className="text-[9px] text-foreground-400 ml-1">Analizando...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef}></div>
                </div>

                <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-background-200 shrink-0 flex items-center gap-2">
                  <input
                    type="text"
                    value={queryInput}
                    onChange={(e) => setQueryInput(e.target.value)}
                    placeholder="Preguntá algo sobre la plataforma..."
                    className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-[10px] text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                    disabled={isThinking}
                  />
                  <button
                    type="submit"
                    disabled={!queryInput.trim() || isThinking}
                    className="w-9 h-9 rounded-xl bg-foreground-950 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-40 shrink-0"
                  >
                    <i className="ri-send-plane-fill text-background-50 text-sm"></i>
                  </button>
                </form>
              </div>

              {/* History Panel */}
              <div className="bg-background-50 border border-background-200 rounded-2xl flex flex-col h-[350px] lg:h-[620px]">
                <div className="px-4 py-3 border-b border-background-200 shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground-800 text-xs font-semibold">Historial</p>
                      <p className="text-foreground-400 text-[10px]">{history.length} consultas</p>
                    </div>
                    {selectedHistoryId && (
                      <button
                        onClick={() => { setSelectedHistoryId(null); setChatHistory([]); }}
                        className="text-foreground-300 hover:text-foreground-500 transition-colors cursor-pointer"
                        title="Nueva consulta"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {history.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-background-100 flex items-center justify-center">
                        <i className="ri-history-line text-foreground-300 text-lg"></i>
                      </div>
                      <p className="text-foreground-400 text-[10px]">Sin consultas aún</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-background-200">
                      {history.map((entry) => (
                        <div
                          key={entry.id}
                          onClick={() => loadFromHistory(entry)}
                          className={`px-3 py-2.5 cursor-pointer transition-colors group ${
                            selectedHistoryId === entry.id
                              ? 'bg-background-200'
                              : 'hover:bg-background-100'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${aiTypeColor[entry.type].split(' ')[0]}`}>
                              <i className={`${aiTypeIcon[entry.type]} text-[11px] ${aiTypeColor[entry.type].split(' ')[1]}`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold text-foreground-800 truncate">{entry.title}</span>
                              </div>
                              <p className="text-[9px] text-foreground-500 truncate mt-0.5">{entry.query}</p>
                              <p className="text-[8px] text-foreground-300 mt-1">{entry.timestamp}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteHistoryItem(entry.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-foreground-300 hover:text-red-400 cursor-pointer shrink-0"
                              title="Eliminar"
                            >
                              <i className="ri-delete-bin-line text-[11px]"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · AI Assistant</p>
      </div>
    </div>
  );
}